import {
  protectedAxios,
  protectedContentAxios,
  publicContentAxios,
} from '@/lib/axiosInstance';
import { PaginatedApiRequest } from '@/types/api/common';
import {
  CreateStoryRequest,
  CreateStoryResponse,
  DeleteStoryRequest,
  DeleteStoryResponse,
  GetPresignedUrlResponse,
  GetStoryRequest,
  GetStoryResponse,
  StoryListingResponse,
  UpdateStoryRequest,
  UpdateStoryResponse,
} from '@/types/api/stories';

export const createStory = async (
  payload: CreateStoryRequest
): Promise<CreateStoryResponse> => {
  const response = await protectedContentAxios.post<CreateStoryResponse>(
    '/stories',
    payload
  );

  return response.data;
};

export const updateStory = async (
  payload: UpdateStoryRequest
): Promise<UpdateStoryResponse> => {
  const response = await protectedContentAxios.put<UpdateStoryResponse>(
    `/stories/${payload.id}`,
    payload
  );

  return response.data;
};

export const deleteStory = async (
  payload: DeleteStoryRequest
): Promise<DeleteStoryResponse> => {
  const response = await protectedContentAxios.delete<DeleteStoryResponse>(
    `/stories/${payload.storyId}`
  );

  return response.data;
};

export const getStories = async (
  payload: Partial<GetStoryRequest>
): Promise<GetStoryResponse> => {
  const queryString = new URLSearchParams({
    questionId: payload.questionId || '',
    parentStoryId: payload.parentStoryId || '',
    page: payload.page?.toString() || '1',
    limit: payload.limit?.toString() || '10',
    categoryId: payload.categoryId || '',
    authorId: payload.authorId || '',
    focusedStoryId: payload.focusedStoryId || '',
  }).toString();

  const response = await publicContentAxios.get<GetStoryResponse>(
    `/stories?${queryString}`
  );

  return response.data;
};

// TODO: Need to discuss with the team about the endpoint
export const storyListing = async (
  payload: PaginatedApiRequest
): Promise<StoryListingResponse> => {
  const queryString = new URLSearchParams({
    page: payload.page.toString(),
    limit: payload.limit.toString(),
  }).toString();

  const response = await protectedAxios.get<StoryListingResponse>(
    `/user/questions/by-category?${queryString}`
  );

  return response.data;
};

// export const getStory = async ()

export const trackStoryView = async (storyId: string) => {
  const payload = {
    storyId,
  };
  const response = await publicContentAxios.post(`/stories/track`, payload);

  return response.data;
};

export const getRelatedStories = async (
  categoryId: string,
  limit: number = 5,
  theme?: string
): Promise<GetStoryResponse> => {
  console.log(
    `[getRelatedStories] Fetching related stories for category: ${categoryId}, theme: ${theme}, limit: ${limit}`
  );

  // If theme is provided, use Elasticsearch for better theme matching
  if (theme && process.env.NEXT_PUBLIC_ENHANCED_SEARCH_ENABLED === 'true') {
    try {
      console.log(
        `[getRelatedStories] Using Elasticsearch for theme-based matching`
      );

      // Import the search service axios
      const { searchServiceAxios } = await import('@/lib/searchServiceAxios');
      const { getCurrentTenantId } = await import('@/lib/tenant');

      const tenantId = getCurrentTenantId();

      // Use the new related content endpoint
      const searchParams = {
        theme: theme,
        type: 'story',
        categoryId: categoryId,
        tenantId: tenantId,
        limit: limit,
      };

      const response = await searchServiceAxios.get('/search/related', {
        params: searchParams,
        timeout: 5000,
      });

      if (response.data.success && response.data.data.hits) {
        const stories = response.data.data.hits.map((hit: any) => ({
          id: hit._source.id,
          title: hit._source.title,
          theme: hit._source.theme,
          author: hit._source.author,
          authorId: hit._source.authorId,
          createdDate: hit._source.createdDate,
          isAnonymous: hit._source.isAnonymous || false,
          isReported: hit._source.isReported || false,
          content: hit._source.content,
          outcome: hit._source.outcome,
          keywords: hit._source.keywords || hit._source.tags,
          category: hit._source.category,
          viewCount: hit._source.viewCount || 0,
          commentCount: hit._source.commentCount || 0,
          subStories: hit._source.subStories || [],
          questionId: hit._source.questionId,
          questionTitle: hit._source.questionTitle,
          isMoreSubStoriesAvailable: false,
          question: hit._source.question,
        }));

        console.log(
          `[getRelatedStories] Elasticsearch returned ${stories.length} stories`
        );
        return {
          statusCode: 200,
          message: 'Success',
          data: stories.slice(0, limit),
          totalCount: stories.length,
          page: 1,
          limit: limit,
          success: true,
        };
      }
    } catch (error) {
      console.warn(
        `[getRelatedStories] Elasticsearch failed, falling back to traditional API:`,
        error
      );
    }
  }

  // Fallback to traditional API
  const queryParams = new URLSearchParams({
    page: '1',
    limit: String(limit),
    categoryId: categoryId,
  });

  const response = await publicContentAxios.get<GetStoryResponse>(
    `/stories?${queryParams}`
  );

  console.log(
    `[getRelatedStories] Traditional API returned ${response.data.data?.length || 0} stories`
  );
  return response.data;
};

// ✅ NEW: Get stories from questions in the same category
export const getStoriesFromCategoryQuestions = async (
  categoryId: string,
  limit: number = 5
): Promise<GetStoryResponse> => {
  console.log(
    `[getStoriesFromCategoryQuestions] Fetching stories from questions in category: ${categoryId}, limit: ${limit}`
  );

  try {
    // First, get questions from the same category
    const questionsQueryParams = new URLSearchParams({
      page: '1',
      limit: '10', // Get more questions to find stories
      categoryId: categoryId,
      status: 'PUBLISHED',
    });

    const questionsResponse = await publicContentAxios.get(
      `/questions?${questionsQueryParams}`
    );

    if (
      !questionsResponse.data.data ||
      questionsResponse.data.data.length === 0
    ) {
      console.log(
        `[getStoriesFromCategoryQuestions] No questions found in category: ${categoryId}`
      );
      return {
        statusCode: 200,
        message: 'Success',
        data: [],
        totalCount: 0,
        page: 1,
        limit: limit,
        success: true,
      };
    }

    // Get question IDs from the same category
    const questionIds = questionsResponse.data.data.map((q: any) => q.id);
    console.log(
      `[getStoriesFromCategoryQuestions] Found ${questionIds.length} questions in category: ${categoryId}`
    );

    // ✅ FIXED: Make separate API calls for each question ID since backend doesn't support multiple questionId values
    const allStories: any[] = [];

    // Process question IDs in batches to avoid too many concurrent requests
    const batchSize = 3; // Process 3 questions at a time
    for (let i = 0; i < questionIds.length; i += batchSize) {
      const batch = questionIds.slice(i, i + batchSize);

      const batchPromises = batch.map(async (questionId: string) => {
        try {
          const storiesQueryParams = new URLSearchParams({
            page: '1',
            limit: String(Math.ceil(limit / questionIds.length)), // Distribute limit across questions
          });

          // Add single questionId as filter
          storiesQueryParams.append('questionId', questionId);

          const storiesResponse =
            await publicContentAxios.get<GetStoryResponse>(
              `/stories?${storiesQueryParams}`
            );

          return storiesResponse.data.data || [];
        } catch (error) {
          console.warn(
            `[getStoriesFromCategoryQuestions] Failed to fetch stories for question ${questionId}:`,
            error
          );
          return [];
        }
      });

      const batchResults = await Promise.all(batchPromises);
      allStories.push(...batchResults.flat());
    }

    // Remove duplicates and limit results
    const uniqueStories = allStories
      .filter(
        (story, index, self) =>
          index === self.findIndex((s) => s.id === story.id)
      )
      .slice(0, limit);

    console.log(
      `[getStoriesFromCategoryQuestions] Found ${uniqueStories.length} unique stories from category questions`
    );

    return {
      statusCode: 200,
      message: 'Success',
      data: uniqueStories,
      totalCount: uniqueStories.length,
      page: 1,
      limit: limit,
      success: true,
    };
  } catch (error) {
    console.error(
      `[getStoriesFromCategoryQuestions] Error fetching stories from category questions:`,
      error
    );
    return {
      statusCode: 500,
      message: 'Error fetching stories from category questions',
      data: [],
      totalCount: 0,
      page: 1,
      limit: limit,
      success: false,
    };
  }
};

export const getPresignedUrl = async (fileType: string) => {
  const response = await protectedContentAxios.post<GetPresignedUrlResponse>(
    `/stories/presigned-url`,
    {
      fileType,
    }
  );

  return response.data;
};
